import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageContainer from '../components/PageContainer';
import AssistantList from '../components/assistants/AssistantList';
import Modal from '../components/Modal';
import { API_BASE_URL } from '../config/api';
import { isKnowledgeTool } from '../utils/toolVisibility';
import { useSnackbar } from 'notistack';
import '../styles/assistants.css';
import { User, Book, Tool, FileText, MessageSquare, Layout, Search, Plus } from 'react-feather';

const AGENT_TEMPLATE_OPTIONS = [
  {
    value: 'default_agent',
    label: 'Web + Phone Agent',
    agentType: 'customer',
  },
  {
    value: 'business_agent_template',
    label: 'Business Assistant',
    agentType: 'business',
  },
  {
    value: 'vanilla_template',
    label: 'Vanilla Agent',
    agentType: 'customer',
  },
];

const getAgentTypeForTemplate = (templateName) =>
  AGENT_TEMPLATE_OPTIONS.find((option) => option.value === templateName)?.agentType || 'customer';

const Assistants = () => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [greeting, setGreeting] = useState('');
  const [knowledgeList, setKnowledgeList] = useState([]);
  const [selectedKnowledgeId, setSelectedKnowledgeId] = useState('');
  const [toolList, setToolList] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('default_agent');

  const { enqueueSnackbar } = useSnackbar();

  const fetchAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "true"
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setAssistants([]);
          return;
        }
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} with error ${errorText}`);
      }

      const data = await response.json();
      setAssistants(data || []);
    } catch (error) {
      enqueueSnackbar('Failed to load assistants', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleUpdate = useCallback(async () => {
    fetchAssistants();
  }, [fetchAssistants]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  useEffect(() => {
    if (!createOpen) return;
    const fetchMeta = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const headers = {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
            };
            const [kbRes, toolsRes] = await Promise.all([
              fetch(`${API_BASE_URL}/api/knowledge`, { headers }),
              fetch(`${API_BASE_URL}/api/tools`, { headers }),
            ]);
            if (!kbRes.ok) throw new Error('Failed to load knowledge bases');
            if (!toolsRes.ok) throw new Error('Failed to load tools');
            const kbData = await kbRes.json();
            const toolsData = await toolsRes.json();
            setKnowledgeList(kbData || []);
            const allTools = toolsData || [];
            setToolList(allTools.filter(tool => !isKnowledgeTool(tool)));
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to load metadata', { variant: 'error' });
        }
    };
    fetchMeta();
  }, [createOpen, enqueueSnackbar]);

  // Search over name and greeting so an agent can be found by what it says as
  // well as what it is called.
  const visibleAssistants = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inUse = (assistants || []).filter((a) => a.inuse !== false);
    if (!q) return inUse;
    return inUse.filter((a) => {
      const agentName = (a.name || '').toLowerCase();
      const agentGreeting = (a.greeting || '').toLowerCase();
      return agentName.includes(q) || agentGreeting.includes(q);
    });
  }, [assistants, query]);

  const resetForm = () => {
    setName('');
    setInstructions('');
    setGreeting('');
    setSelectedKnowledgeId('');
    setSelectedTools([]);
    setSelectedTemplate('default_agent');
  };

  const handleToggleTool = (toolId) => {
    setSelectedTools((prev) => (
      prev.includes(toolId)
        ? prev.filter((id) => id !== toolId)
        : [...prev, toolId]
    ));
  };

  const handleOpenCreate = () => { resetForm(); setCreateOpen(true); };
  const handleCloseCreate = () => { if (!saving) setCreateOpen(false); };

  const handleCreateAssistant = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Name is required', { variant: 'warning' });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('access_token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      };

      const selectedKb = knowledgeList.find(
        (kb) => String(kb.id) === String(selectedKnowledgeId)
      );

      const selectedGeneralToolIds = [
        ...new Set(
          toolList
            .filter((tool) => selectedTools.includes(tool.id))
            .map((tool) => tool.id)
            .filter(Boolean)
        ),
      ];

      const payload = {
        template_name: selectedTemplate,
        agent_name: name.trim(),
        agent_type: getAgentTypeForTemplate(selectedTemplate),
        instructions: instructions?.trim() || undefined,
        greeting: greeting?.trim() || undefined,
        tools: [],
      };

      const response = await fetch(`${API_BASE_URL}/api/agents`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Create assistant failed:', text);
        throw new Error(text || 'Failed to create assistant');
      }

      const createdAgent = await response.json();
      const assistantId = createdAgent?.id;

      if (!assistantId) {
        throw new Error('Assistant created but no id was returned.');
      }

      // The agent now exists on the server. Linking tools and knowledge are
      // separate calls that can fail independently, so from here on a failure
      // must NOT be reported as "failed to create", the agent is real and
      // throwing away the success would leave the user thinking nothing
      // happened while an agent sits half-configured in the list. Collect what
      // failed instead and report it as a partial result.
      const linkFailures = [];

      for (const toolId of selectedGeneralToolIds) {
        try {
          const linkResponse = await fetch(
            `${API_BASE_URL}/api/${assistantId}/tools/link`,
            {
              method: 'POST',
              headers,
              body: JSON.stringify({ tool_id: toolId }),
            }
          );

          if (!linkResponse.ok) {
            const linkErrText = await linkResponse.text();
            console.error(`Failed to link tool ${toolId}:`, linkErrText);
            const toolName = toolList.find((t) => t.id === toolId)?.display_name
              || toolList.find((t) => t.id === toolId)?.name
              || `tool ${toolId}`;
            linkFailures.push(toolName);
          }
        } catch (linkErr) {
          console.error(`Failed to link tool ${toolId}:`, linkErr);
          linkFailures.push(`tool ${toolId}`);
        }
      }

      let knowledgeFailed = false;
      if (selectedKb?.id) {
        try {
          const knowledgeLinkResponse = await fetch(
            `${API_BASE_URL}/api/knowledge/${selectedKb.id}/agents/${assistantId}`,
            {
              method: 'POST',
              headers,
            }
          );

          if (!knowledgeLinkResponse.ok) {
            const knowledgeErrText = await knowledgeLinkResponse.text();
            console.error('Failed to link knowledge:', knowledgeErrText);
            knowledgeFailed = true;
          }
        } catch (knowledgeErr) {
          console.error('Failed to link knowledge:', knowledgeErr);
          knowledgeFailed = true;
        }
      }

      // Close and refresh regardless, the agent exists either way, and the
      // list is the user's proof of that.
      setCreateOpen(false);
      resetForm();
      await handleUpdate();

      if (linkFailures.length || knowledgeFailed) {
        const parts = [];
        if (linkFailures.length) {
          parts.push(
            `${linkFailures.length} tool${linkFailures.length === 1 ? '' : 's'} could not be linked`
          );
        }
        if (knowledgeFailed) parts.push('the knowledge base could not be linked');
        enqueueSnackbar(
          `Agent "${name.trim()}" was created, but ${parts.join(' and ')}. You can link them from the agent's settings.`,
          { variant: 'warning', autoHideDuration: 8000 }
        );
      } else {
        enqueueSnackbar('Agent created successfully', { variant: 'success' });
      }
    } catch (err) {
      console.error('Create assistant error:', err);
      enqueueSnackbar(err.message || 'Failed to create assistant', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assistantId) => {
    if (!window.confirm('Are you sure you want to delete this assistant? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/agents/${assistantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "true"
        },
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to delete assistant');
      }

      enqueueSnackbar('Agent deleted successfully', { variant: 'success' });
      await fetchAssistants();

    } catch (error) {
      console.error('Delete error:', error);
      enqueueSnackbar('Failed to delete agent', { variant: 'error' });
    }
  };

  const totalInUse = (assistants || []).filter((a) => a.inuse !== false).length;

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading agents..."
      title="Agents"
      subtitle="Manage Your AI Workforce"
    >
      <div className="assistants-shell">
        <div className="assistants-surface">
          <div className="assistants-toolbar">
            <h2 className="assistants-toolbar-title">
              Your Agents
              {totalInUse > 0 && (
                <span className="assistants-count">{totalInUse}</span>
              )}
            </h2>

            <div className="assistants-toolbar-actions">
              {totalInUse > 0 && (
                <div className="assistants-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search agents"
                    aria-label="Search agents"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={handleOpenCreate}
                className="assistants-btn-primary-pill"
              >
                <Plus size={18} aria-hidden="true" />
                New Agent
              </button>
            </div>
          </div>

          <div className="assistants-list-wrap">
            <AssistantList
              assistants={visibleAssistants}
              loading={loading}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onCreate={handleOpenCreate}
              isFiltered={query.trim().length > 0}
              onClearFilter={() => setQuery('')}
            />
          </div>
        </div>
      </div>

      {/* Create dialog. Uses the shared Modal so Esc-to-close, background
          scroll lock, backdrop click and the dialog ARIA roles all come for
          free, the previous hand-rolled backdrop put role="dialog" on the
          backdrop itself and handled none of those. */}
      <Modal
        isOpen={createOpen}
        onClose={handleCloseCreate}
        title="Create new agent"
      >
        <div className="assistants-form-card">
          <p className="assistants-form-intro">
            Configure identity, knowledge, and capabilities. Everything here can be
            changed later from the agent's settings.
          </p>

          <div className="assistants-form-grid">
            <div className="assistants-form-group">
              <label className="assistants-label" htmlFor="agent-name">
                <span className="assistants-label-icon"><User size={12} /></span>
                Name
              </label>
              <input
                id="agent-name"
                className="assistants-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Sales Agent"
              />
            </div>

            <div className="assistants-form-group">
              <label className="assistants-label" htmlFor="agent-greeting">
                <span className="assistants-label-icon"><MessageSquare size={12} /></span>
                Greeting
              </label>
              <input
                id="agent-greeting"
                className="assistants-input"
                type="text"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Hi! How can I help?"
              />
              <span className="assistants-hint">The first thing the agent says.</span>
            </div>

            <div className="assistants-form-group">
              <label className="assistants-label" htmlFor="agent-template">
                <span className="assistants-label-icon"><Layout size={12} /></span>
                Template
              </label>
              <select
                id="agent-template"
                className="assistants-select"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                {AGENT_TEMPLATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="assistants-hint">Sets the channels the agent starts with.</span>
            </div>

            <div className="assistants-form-group">
              <label className="assistants-label" htmlFor="agent-knowledge">
                <span className="assistants-label-icon"><Book size={12} /></span>
                Knowledge Base
              </label>
              <select
                id="agent-knowledge"
                className="assistants-select"
                value={selectedKnowledgeId}
                onChange={(e) => setSelectedKnowledgeId(e.target.value)}
              >
                <option value="">None</option>
                {knowledgeList.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name || (kb.config && kb.config.name) || `Dataset #${kb.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="assistants-form-group full">
              <label className="assistants-label" htmlFor="agent-instructions">
                <span className="assistants-label-icon"><FileText size={12} /></span>
                Instructions
              </label>
              <textarea
                id="agent-instructions"
                className="assistants-textarea"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={5}
                placeholder="Describe what this agent should do, tone, rules, etc."
              />
            </div>

            <div className="assistants-form-group full">
              <label className="assistants-label">
                <span className="assistants-label-icon"><Tool size={12} /></span>
                Tools
                {selectedTools.length > 0 && (
                  <span className="assistants-label-count">{selectedTools.length} selected</span>
                )}
              </label>

              {toolList.length === 0 ? (
                <div className="assistants-tools-empty">
                  <Tool size={16} aria-hidden="true" />
                  No additional tools available.
                </div>
              ) : (
                <div className="assistants-tools-grid">
                  {toolList.map((tool) => {
                    const isSelected = selectedTools.includes(tool.id);
                    const toolName = tool.display_name || tool.name;
                    return (
                      <label
                        key={tool.id || tool.name}
                        className={`assistants-tool-pill${isSelected ? ' selected' : ''}`}
                        title={toolName}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleTool(tool.id)}
                        />
                        <span className="assistants-tool-pill-check" aria-hidden="true" />
                        <span className="assistants-tool-pill-name">{toolName}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="assistants-form-actions">
            <button
              type="button"
              className="assistants-btn assistants-btn-secondary"
              onClick={handleCloseCreate}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="assistants-btn assistants-btn-primary"
              onClick={handleCreateAssistant}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="assistants-btn-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Create Agent'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default Assistants;
